// Keep the app's configure widget in charge of hitsPerPage for collections and search.
(() => {
  let pending;
  let loadMoreActive = false;
  let retainedPdpReturn;
  let initialNavigationChecked = false;
  let finishInitialNavigation;
  let returnSession;

  const returnKey = 'oztrail.algolia.pdpReturn.v1';
  const canonical = value => JSON.stringify(value, (_key, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
  const responseKey = query => {
    const params = query.params || {};
    // The app regenerates anonymous analytics IDs when a document is rebuilt.
    // Keep identity in the key for signed-in users and personalized searches.
    if (/^anonymous-/.test(params.userToken || '') && params.enablePersonalization !== true && params.enablePersonalization !== 'true') {
      const { userToken: _anonymous, ...rest } = params;
      return canonical({ ...query, params: rest });
    }
    return canonical(query);
  };
  const clone = value => JSON.parse(JSON.stringify(value));

  function getReturnSession() {
    if (returnSession) return returnSession;
    const responses = new Map();
    const wrappedClients = new WeakSet();
    const marker = window.history.state?.oztrailAlgoliaReturn;
    const back = window.performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward';
    const valid = marker && marker.url === window.location.href && marker.mode === window.algoliaResultsNavigation
      && Number.isFinite(marker.y) && Number.isFinite(marker.offset)
      && Number.isInteger(marker.firstPage) && Number.isInteger(marker.lastPage)
      && marker.firstPage >= 0 && marker.lastPage >= marker.firstPage && marker.lastPage < 1000
      && Number.isFinite(marker.savedAt) && Date.now() - marker.savedAt >= 0;
    const bookmark = back && valid ? marker : undefined;
    let restoring = Boolean(bookmark);
    if (bookmark && Date.now() - bookmark.savedAt < 5 * 60 * 1000) {
      try {
        const raw = window.sessionStorage.getItem(returnKey);
        const saved = JSON.parse(raw);
        if (saved?.id === bookmark.id && Array.isArray(saved.responses)) {
          saved.responses.slice(-100).forEach(([key, entry]) => {
            if (typeof key === 'string' && Array.isArray(entry?.result?.hits)) responses.set(key, entry);
          });
        }
      } catch (_error) { /* Missing, blocked or full storage falls back to Algolia. */ }
    }
    returnSession = {
      bookmark,
      finish() { restoring = false; },
      clearBookmark() {
        const state = window.history.state;
        if (state?.oztrailAlgoliaReturn) {
          const { oztrailAlgoliaReturn: _bookmark, ...rest } = state;
          window.history.replaceState(rest, document.title);
        }
      },
      wrap(client) {
        if (!client?.search || wrappedClients.has(client)) return client;
        const wrapped = { ...client, async search(queries, requestOptions) {
          const results = new Array(queries.length);
          const missing = [];
          queries.forEach((query, index) => {
            const key = responseKey(query);
            const entry = responses.get(key);
            if (restoring && entry && Date.now() - entry.at >= 0 && Date.now() - entry.at < 5 * 60 * 1000) results[index] = clone(entry.result);
            else missing.push({ query, index, key });
          });
          if (missing.length) {
            const response = await client.search(missing.map(item => item.query), requestOptions);
            missing.forEach(({ index, key }, offset) => {
              results[index] = response.results[offset];
              responses.set(key, { at: Date.now(), result: clone(results[index]) });
            });
            while (responses.size > 100) responses.delete(responses.keys().next().value);
            return { ...response, results };
          }
          return { results };
        } };
        wrappedClients.add(wrapped);
        return wrapped;
      },
      save(position, state) {
        if (!state?.items.length || !state.results) return;
        const size = state.results.hitsPerPage;
        const firstPage = Math.floor((state.items[0].__position - 1) / size);
        const lastPage = Math.floor((state.items[state.items.length - 1].__position - 1) / size);
        if (!Number.isInteger(firstPage) || !Number.isInteger(lastPage)) return;
        const saved = { ...position, firstPage, lastPage, savedAt: Date.now(), mode: window.algoliaResultsNavigation,
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}` };
        // The small history bookmark still works when session storage is unavailable.
        window.history.replaceState({ ...window.history.state, oztrailAlgoliaReturn: saved }, document.title);
        try {
          const data = JSON.stringify({ id: saved.id, responses: [...responses] });
          if (data.length <= 1500000) window.sessionStorage.setItem(returnKey, data);
          else window.sessionStorage.removeItem(returnKey);
        } catch (_error) { /* Re-fetch only the unavailable responses on Back. */ }
      },
    };
    return returnSession;
  }

  function configureSearchClient(options) {
    if (!['load_more', 'infinite_scroll'].includes(window.algoliaResultsNavigation)) return options;
    return { ...options, searchClient: getReturnSession().wrap(options.searchClient) };
  }

  function isAvailable(app) {
    return ['load_more', 'infinite_scroll'].includes(window.algoliaResultsNavigation)
      && typeof window.oztrailAlgoliaWidgets?.infiniteHits === 'function'
      && (!app.externals?.instantsearch?.version || app.externals.instantsearch.version === '4.102.0');
  }

  function resetInitialNavigation() {
    if (initialNavigationChecked) return;
    initialNavigationChecked = true;
    if (!['load_more', 'infinite_scroll'].includes(window.algoliaResultsNavigation)) return;
    const navigationType = window.performance.getEntriesByType?.('navigation')[0]?.type;
    // Back/Forward owns its existing route and, when retained, its accumulated grid.
    if (navigationType === 'back_forward') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    // Remove the page suffix emitted by theme.liquid for the original request.
    document.title = document.title.replace(/ – Page \d+$/, '');
    window.history.replaceState(window.history.state, document.title, url);

    // Suppress late native reload restoration while asynchronous results build.
    // User input or departure restores native handling before PDP Back navigation.
    const restoration = window.history.scrollRestoration;
    if (restoration !== undefined) window.history.scrollRestoration = 'manual';
    let frame;
    const inputEvents = ['pointerdown', 'wheel', 'touchmove', 'keydown'];
    const finish = () => {
      window.cancelAnimationFrame(frame);
      if (restoration !== undefined) window.history.scrollRestoration = restoration;
      window.removeEventListener('pageshow', resetScroll);
      window.removeEventListener('pagehide', finish);
      inputEvents.forEach(type => window.removeEventListener(type, finish, true));
    };
    const resetScroll = event => {
      if (event?.persisted) { finish(); return; }
      const scrollToHeading = () => {
        const heading = document.querySelector('#MainContent .collection-hero__title');
        const header = document.querySelector('.section-header');
        const stickyHeader = header?.querySelector('sticky-header');
        if (typeof stickyHeader?.reveal === 'function') stickyHeader.reveal();
        const top = heading
          ? Math.max(0, window.scrollY + heading.getBoundingClientRect().top - (stickyHeader ? header.offsetHeight : 0)
            - (parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--ts-h')) || 0))
          : 0;
        window.scrollTo({ top, behavior: 'instant' });
      };
      scrollToHeading();
      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(() => {
          scrollToHeading();
          frame = undefined;
        });
      });
    };
    finishInitialNavigation = finish;
    window.addEventListener('pagehide', finish);
    inputEvents.forEach(type => window.addEventListener(type, finish, { passive: true, capture: true }));
    if (document.readyState === 'complete') resetScroll();
    else window.addEventListener('pageshow', resetScroll, { once: true });
  }

  function configureRouting(options, app) {
    if (!isAvailable(app)) return options;
    options = configureSearchClient(options);
    resetInitialNavigation();
    const history = app.externals?.instantsearch?.routers?.history || window.oztrailAlgoliaWidgets?.history;
    if (!history || options.routing === false || options.routing?.router) return options;

    let routeState;
    const router = history({
      createURL({ qsModule, routeState: nextState, location }) {
        const url = new URL(location.href);
        const original = new URLSearchParams(url.search);
        url.search = qsModule.stringify(nextState);
        const resultParams = new Set(['page', 'q', 'query', 'sortBy', 'hitsPerPage', 'configure',
          'refinementList', 'menu', 'hierarchicalMenu', 'range', 'numericMenu', 'toggle', 'ratingMenu', 'geoSearch']);
        for (const [key, value] of original) {
          if (!resultParams.has(key.split('[')[0]) && !Object.prototype.hasOwnProperty.call(nextState, key.split('[')[0])) {
            url.searchParams.append(key, value);
          }
        }
        return url.href;
      },
      push(url) {
        const current = new URL(window.location.href);
        const next = new URL(url, current);
        current.searchParams.delete('page');
        next.searchParams.delete('page');
        current.searchParams.sort();
        next.searchParams.sort();
        // Compare the complete URL so filters, sorting, queries and other parameters
        // still create history entries, including changes combined with a new page.
        // Canonical encoding/order changes on startup also replace the same entry.
        const replace = loadMoreActive && current.href === next.href;
        window.history[replace ? 'replaceState' : 'pushState'](routeState, document.title, url);
      },
    });
    // Preserve the same history.state payload as the standard Algolia router.
    const write = router.write.bind(router);
    router.write = state => { routeState = state; write(state); };
    const onUpdate = router.onUpdate.bind(router);
    router.onUpdate = callback => onUpdate(state => {
      // A retained PDP return already has the exact URL, helper state and grid.
      // Reapplying that route would unnecessarily run InstantSearch again.
      if (!retainedPdpReturn?.isReturning()) callback(state);
    });
    return { ...options, routing: { ...options.routing, router } };
  }

  function prepare(app, defaultWidgets) {
    if (!isAvailable(app)) return [];
    const factory = window.oztrailAlgoliaWidgets?.infiniteHits;
    const search = app.instantsearch?.search;
    const hits = defaultWidgets.find(widget => widget.$$widgetType === 'ais.hits');
    const pagination = defaultWidgets.find(widget => widget.$$widgetType === 'ais.pagination');
    const templates = app.instantsearch?.templates;
    if (!factory || !search || !hits || !pagination || !templates) return [];

    try {
      // Reuse the initialized app widget's transform, including its pricing and attribution data.
      const { widgetParams } = hits.getWidgetRenderState({
        helper: search.helper,
        instantSearchInstance: search,
      });
      if (typeof widgetParams.transformItems !== 'function'
        || typeof templates.instantSearchProductTemplate !== 'function'
        || typeof templates.instantSearchNoResultTemplate !== 'function') return [];

      const labels = window.algoliaLoadMoreLabels;
      const container = document.querySelector('.ais-hits-container');
      if (!labels?.more || !container || !document.querySelector('[data-algolia-load-more-status]')
        || !document.querySelector('[data-algolia-load-more-retry]')) return [];
      const widget = factory({
        container,
        escapeHTML: widgetParams.escapeHTML,
        transformItems: widgetParams.transformItems,
        showPrevious: true,
        cssClasses: {
          list: 'ais-Hits-list',
          item: 'ais-Hits-item',
          loadMore: 'button button--secondary oztrail-load-more-button',
          loadPrevious: 'button button--secondary oztrail-load-more-button',
        },
        templates: {
          item: (hit, { html, components }) => templates.instantSearchProductTemplate(hit, html, components, app),
          empty: (_results, { html }) => templates.instantSearchNoResultTemplate(html, app),
          showMoreText: () => labels.more,
          showPreviousText: () => labels.previous,
        },
      });

      const render = widget.render;
      widget.render = function(options) {
        container.querySelectorAll('.ais-Hits-item--promo').forEach(tile => tile.remove());
        render.call(this, options);
      };

      pending = { search, widget, originals: [hits, pagination], uiState: search.getUiState(), labels };
      return pending.originals;
    } catch (error) {
      console.warn('Unable to prepare Algolia Load more; keeping pagination.', error);
      return [];
    }
  }

  function createPdpReturn(container, getContext, resumeAutoLoad, getState, requestPage) {
    const session = getReturnSession();
    let recovery = session.bookmark;
    let requestedRange;
    let bookmark;
    let departed = false;
    let paused = Boolean(recovery);
    let pauseContext;
    let frame;
    let settleTimer;
    const cancelChecks = () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      frame = undefined;
      settleTimer = undefined;
    };
    const reset = () => {
      cancelChecks();
      bookmark = undefined;
      recovery = undefined;
      departed = false;
      paused = false;
      pauseContext = undefined;
    };
    const visibleTop = () => {
      const header = document.querySelector('.section-header');
      const toolbar = document.querySelector('.ais-button-container');
      const toolbarVisible = toolbar && window.getComputedStyle(toolbar).display !== 'none';
      const streamer = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--ts-h')) || 0;
      return Math.max(streamer, header?.getBoundingClientRect().bottom || 0,
        toolbarVisible ? toolbar.getBoundingClientRect().bottom : 0);
    };
    const correctPosition = () => {
      frame = undefined;
      if (!bookmark) return;
      if (!bookmark.card.isConnected || bookmark.context !== getContext() || bookmark.url !== window.location.href) {
        reset();
        return;
      }
      const rect = bookmark.card.getBoundingClientRect();
      if (!rect.height || Math.abs(window.scrollY - bookmark.y) <= 2 || Math.abs(rect.top - bookmark.top) <= 2) return;
      const top = Math.max(0, window.scrollY + rect.top - visibleTop() - bookmark.offset);
      if (Math.abs(window.scrollY - top) > 2) window.scrollTo({ top, behavior: 'instant' });
    };
    const onProductClick = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      const card = link?.closest('.ais-hit.ais-product');
      if (!card || !container.contains(card) || link.hasAttribute('download')
        || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !/\/products\/[^/]+/.test(url.pathname)) return;
      cancelChecks();
      const top = card.getBoundingClientRect().top;
      pauseContext = getContext();
      bookmark = { card, context: pauseContext, url: window.location.href, y: window.scrollY, top, offset: top - visibleTop() };
      bookmark.product = url.pathname;
      bookmark.objectID = card.dataset.algoliaObjectid || card.dataset.objectid;
      paused = true;
      departed = false;
    };
    const onPageHide = () => {
      if (!bookmark || bookmark.context !== getContext()) return;
      cancelChecks();
      bookmark.url = window.location.href;
      const { card: _card, context: _context, ...position } = bookmark;
      session.save(position, getState());
      departed = true;
    };
    const onPageShow = event => {
      if (!event.persisted) { if (!session.bookmark) reset(); return; }
      if (!departed || !bookmark) return;
      departed = false;
      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(correctPosition);
      });
      settleTimer = window.setTimeout(correctPosition, 300);
    };
    const onUserInput = event => {
      if (!paused) return;
      cancelChecks();
      bookmark = undefined;
      recovery = undefined;
      session.finish();
      session.clearBookmark();
      departed = false;
      if (event.type === 'pointerdown') return;
      if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
      paused = false;
      pauseContext = undefined;
      resumeAutoLoad();
    };
    const inputEvents = ['wheel', 'touchmove', 'pointerdown', 'keydown'];
    container.addEventListener('click', onProductClick, true);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    inputEvents.forEach(type => window.addEventListener(type, onUserInput, { passive: true, capture: true }));
    return {
      get paused() { return paused; },
      isReturning() { return paused && bookmark?.url === window.location.href && bookmark.context === getContext(); },
      sync({ loading, failed } = {}) {
        if (paused && pauseContext !== undefined && pauseContext !== getContext()) {
          session.finish(); reset(); return;
        }
        if (!recovery || loading || failed) return;
        const state = getState();
        if (!state?.results || !state.items.length) return;
        // The app applies its collection configure widget after status initialization.
        // Only lock the context once the first configured results have settled.
        pauseContext = getContext();
        const size = state.results.hitsPerPage;
        const first = Math.floor((state.items[0].__position - 1) / size);
        const last = Math.floor((state.items[state.items.length - 1].__position - 1) / size);
        const range = `${first}:${last}`;
        const action = first > recovery.firstPage && !state.isFirstPage ? state.showPrevious
          : last < recovery.lastPage && !state.isLastPage ? state.showMore : undefined;
        if (action) {
          if (requestedRange !== range) { requestedRange = range; requestPage(action); }
          return;
        }
        const saved = recovery;
        recovery = undefined;
        session.finish();
        const card = [...container.querySelectorAll('.ais-hit.ais-product')].find(item => saved.objectID
          ? (item.dataset.algoliaObjectid || item.dataset.objectid) === saved.objectID
          : [...item.querySelectorAll('a[href]')].some(link => new URL(link.href).pathname === saved.product));
        if (!card) { window.scrollTo({ top: saved.y, behavior: 'instant' }); return; }
        bookmark = { ...saved, card, context: getContext() };
        frame = window.requestAnimationFrame(() => { frame = window.requestAnimationFrame(correctPosition); });
        settleTimer = window.setTimeout(correctPosition, 300);
      },
      dispose() {
        reset();
        container.removeEventListener('click', onProductClick, true);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
        inputEvents.forEach(type => window.removeEventListener(type, onUserInput, true));
      },
    };
  }

  function createStatusWidget(search, labels) {
    let container;
    let status;
    let retry;
    let failed = false;
    let requestedByButton = false;
    let previousCount = 0;
    let retryAction;
    let requestKey;
    let resultKey;
    let returnPending = false;
    let restoringHistory = false;
    let returnFrame;
    let observer;
    let sentinel;
    let autoFrame;
    let autoTimer;
    let autoTimerKey;
    let autoRequested = false;
    let pdpReturn;
    let automaticKey;
    let highestPage = 0;
    let manualOnly = false;
    let pageHidden = false;
    const configuredLimit = window.algoliaInfiniteScrollBatches;
    const batchLimit = Number.isInteger(configuredLimit) && configuredLimit >= 1 && configuredLimit <= 10
      ? configuredLimit : 3;
    const searchKey = () => JSON.stringify({ ...search.helper.state, page: undefined });
    const getState = () => Object.values(search.renderState).find(value => value.infiniteHits)?.infiniteHits;
    const cancelAutomaticDelay = () => {
      window.clearTimeout(autoTimer);
      autoTimer = undefined;
      autoTimerKey = undefined;
    };
    const loadNearEnd = delayElapsed => {
      autoFrame = undefined;
      const state = getState();
      if (!loadMoreActive || pageHidden || document.hidden || pdpReturn?.paused || manualOnly || autoRequested || failed || returnPending || restoringHistory
        || search.status !== 'idle' || !state?.results || !state.items.length || state.isLastPage
        || !sentinel.isConnected || document.querySelector('.ais-facets__shown')) {
        cancelAutomaticDelay();
        return;
      }
      const rect = sentinel.getBoundingClientRect();
      if (!rect.height || rect.bottom < 0 || rect.top > window.innerHeight) {
        cancelAutomaticDelay();
        return;
      }
      if (delayElapsed !== true) {
        if (autoTimer === undefined) {
          autoTimerKey = searchKey();
          autoTimer = window.setTimeout(() => {
            const key = autoTimerKey;
            cancelAutomaticDelay();
            if (key === searchKey()) loadNearEnd(true);
          }, 500);
        }
        return;
      }
      autoRequested = true;
      requestedByButton = false;
      retryAction = state.showMore;
      requestKey = searchKey();
      state.showMore();
    };
    const scheduleAutoLoad = () => {
      if (observer && autoFrame === undefined) autoFrame = window.requestAnimationFrame(loadNearEnd);
    };

    const returnToResults = () => {
      returnFrame = undefined;
      if (!returnPending || failed || search.status !== 'idle' || !container.isConnected
        || document.querySelector('.ais-facets__shown')) return;
      returnPending = false;
      const target = container.querySelector('.ais-hit.ais-product') || container;
      const header = document.querySelector('.section-header');
      const stickyHeader = header?.querySelector('sticky-header');
      const toolbar = document.querySelector('.ais-button-container');
      const toolbarVisible = toolbar && window.getComputedStyle(toolbar).display !== 'none';
      const streamerHeight = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--ts-h')) || 0;
      const visibleBottom = Math.max(streamerHeight, header?.getBoundingClientRect().bottom || 0,
        toolbarVisible ? toolbar.getBoundingClientRect().bottom : 0);
      const targetTop = target.getBoundingClientRect().top;
      if (targetTop >= visibleBottom - 1) { scheduleAutoLoad(); return; }
      const gap = parseFloat(window.getComputedStyle(container).getPropertyValue('--plp-sticky-gap')) || 0;
      const offset = streamerHeight + (stickyHeader ? header.offsetHeight : 0)
        + (toolbarVisible ? toolbar.getBoundingClientRect().height : 0) + gap;
      const top = Math.max(0, window.scrollY + targetTop - offset);
      if (typeof stickyHeader?.reveal === 'function') stickyHeader.reveal();
      window.scrollTo({ top, behavior: 'instant' });
      scheduleAutoLoad();
    };
    const scheduleReturn = () => {
      if (returnPending && returnFrame === undefined) returnFrame = window.requestAnimationFrame(returnToResults);
    };
    const onPopState = () => {
      cancelAutomaticDelay();
      if (pdpReturn?.isReturning()) return;
      restoringHistory = true;
      returnPending = false;
      window.cancelAnimationFrame(returnFrame);
      returnFrame = undefined;
    };

    const update = () => {
      const state = getState();
      const loading = search.status === 'loading' || search.status === 'stalled';
      if (loading || failed || (autoTimer !== undefined && autoTimerKey !== searchKey())) cancelAutomaticDelay();
      if (!loading && !failed && state?.results) {
        const nextKey = searchKey();
        if (automaticKey !== nextKey) {
          automaticKey = nextKey;
          highestPage = 0;
          manualOnly = false;
        }
        highestPage = Math.max(highestPage, state.results.page + 1);
        if (highestPage >= batchLimit) manualOnly = true;
      }
      if (!loading) autoRequested = false;
      if (loading) {
        failed = false;
        // A new filter/query must never retry the previous result set's page action.
        if (requestKey !== searchKey()) retryAction = undefined;
      }
      container.setAttribute('aria-busy', String(loading));
      retry.hidden = !failed;
      retry.disabled = loading;
      container.querySelectorAll('.oztrail-load-more-button').forEach(button => {
        const more = button.classList.contains('ais-InfiniteHits-loadMore');
        const reachedEnd = more ? state?.isLastPage : state?.isFirstPage;
        button.type = 'button';
        button.disabled = loading || reachedEnd;
        button.hidden = failed || Boolean(reachedEnd) || (more && Boolean(observer) && !manualOnly);
        button.textContent = loading ? labels.loading : more ? labels.more : labels.previous;
      });
      const count = state?.items.length || 0;
      status.hidden = !failed && !count;
      if (failed) status.textContent = labels.error;
      else if (count) {
        status.textContent = (state.results.nbHits === 1 ? labels.showingOne : labels.showing)
          .replace('[count]', count.toLocaleString())
          .replace('[total]', state.results.nbHits.toLocaleString());
        if (state.isLastPage) {
          const limited = state.results.nbHits > state.results.nbPages * state.results.hitsPerPage;
          status.textContent += ` ${limited ? labels.limit : labels.end}`;
        }
        if (loading) status.textContent += ` ${labels.loading}`;
      }
      if (!loading && !failed && retryAction) {
        if (requestedByButton && count > previousCount) {
          container.querySelectorAll('.ais-hit.ais-product')[previousCount]
            ?.querySelector('a')?.focus({ preventScroll: true });
        }
        requestedByButton = false;
        retryAction = undefined;
      }
      if (!loading) previousCount = count;
      if (!loading && !failed && state?.results) {
        const nextKey = searchKey();
        if (resultKey !== undefined && resultKey !== nextKey && !restoringHistory) returnPending = true;
        resultKey = nextKey;
        restoringHistory = false;
        scheduleReturn();
        scheduleAutoLoad();
      }
      pdpReturn?.sync({ loading, failed });
    };
    const onError = () => { failed = true; update(); };
    const onClick = event => {
      const button = event.target.closest('.oztrail-load-more-button');
      if (button) {
        const state = getState();
        requestedByButton = button.classList.contains('ais-InfiniteHits-loadMore');
        retryAction = requestedByButton ? state.showMore : state.showPrevious;
        requestKey = searchKey();
        container.querySelectorAll('.oztrail-load-more-button').forEach(control => { control.disabled = true; });
      }
    };
    const onRetry = () => {
      if (!failed || search.status === 'loading' || search.status === 'stalled') return;
      failed = false;
      retry.disabled = true;
      // InstantSearch rolls helper state back on error. Use the connector's cached
      // boundary to retry the failed batch rather than reloading the previous page.
      if (retryAction) retryAction();
      else search.refresh();
    };
    const onFiltersClosed = () => { scheduleReturn(); scheduleAutoLoad(); };
    const onPageHide = () => {
      pageHidden = true;
      cancelAutomaticDelay();
      window.cancelAnimationFrame(autoFrame);
      autoFrame = undefined;
    };
    const onPageShow = () => { pageHidden = false; scheduleAutoLoad(); };
    const onVisibilityChange = () => {
      if (document.hidden) cancelAutomaticDelay();
      else scheduleAutoLoad();
    };

    return {
      $$type: 'oztrail.loadMoreStatus',
      init() {
        container = document.querySelector('.ais-hits-container');
        status = document.querySelector('[data-algolia-load-more-status]');
        retry = document.querySelector('[data-algolia-load-more-retry]');
        pdpReturn = createPdpReturn(container, searchKey, scheduleAutoLoad, getState, action => {
          requestedByButton = false;
          retryAction = action;
          requestKey = searchKey();
          action();
        });
        retainedPdpReturn = pdpReturn;
        if (window.algoliaResultsNavigation === 'infinite_scroll' && typeof window.IntersectionObserver === 'function') {
          sentinel = document.createElement('div');
          sentinel.className = 'oztrail-infinite-scroll-sentinel';
          sentinel.setAttribute('aria-hidden', 'true');
          status.after(sentinel);
          observer = new window.IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) scheduleAutoLoad();
            else cancelAutomaticDelay();
          }, { rootMargin: '0px' });
          observer.observe(sentinel);
        }
        container.addEventListener('click', onClick, true);
        retry.addEventListener('click', onRetry);
        search.on('render', update);
        search.on('error', onError);
        window.addEventListener('popstate', onPopState);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('pageshow', onPageShow);
        document.addEventListener('visibilitychange', onVisibilityChange);
        document.addEventListener('oztrail:algolia:filters-closed', onFiltersClosed);
        document.addEventListener('oztrail:algolia:filters-opened', cancelAutomaticDelay);
      },
      dispose() {
        loadMoreActive = false;
        container.removeEventListener('click', onClick, true);
        retry.removeEventListener('click', onRetry);
        search.removeListener('render', update);
        search.removeListener('error', onError);
        window.removeEventListener('popstate', onPopState);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        document.removeEventListener('oztrail:algolia:filters-closed', onFiltersClosed);
        document.removeEventListener('oztrail:algolia:filters-opened', cancelAutomaticDelay);
        window.cancelAnimationFrame(returnFrame);
        window.cancelAnimationFrame(autoFrame);
        cancelAutomaticDelay();
        pdpReturn?.dispose();
        finishInitialNavigation?.();
        if (retainedPdpReturn === pdpReturn) retainedPdpReturn = undefined;
        observer?.disconnect();
        sentinel?.remove();
        container.removeAttribute('aria-busy');
        status.hidden = true;
        retry.hidden = true;
      },
    };
  }

  document.addEventListener('algolia.instantSearch.afterInit', () => {
    if (!pending) return;
    const { search, widget, originals, uiState, labels } = pending;
    pending = null;
    try {
      search.addWidgets([widget, createStatusWidget(search, labels)]);
      // Pagination disposal removes page state; restore it after the replacement owns it.
      search.setUiState(uiState);
      loadMoreActive = true;
    } catch (error) {
      loadMoreActive = false;
      console.warn('Unable to start Algolia Load more; restoring pagination.', error);
      const replacements = search.mainIndex.getWidgets().filter(value =>
        value === widget || value.$$type === 'oztrail.loadMoreStatus');
      if (replacements.length) search.removeWidgets(replacements);
      search.addWidgets(originals);
      search.setUiState(uiState);
    }
  });

  window.oztrailAlgoliaLoadMore = { prepare, configureRouting, configureSearchClient, isAvailable };
})();
